// Multi-select pickers: a dropdown of checkboxes with Select All / Clear All and a
// filter box. `MultiPicker` is generic (options = [{key, label}]);
// `CorridorPicker` wraps it for the state's surveyed corridors.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// allMeansEmpty pickers keep [] for "all", so "none" needs its own value: Clear All sets [NONE]
// (it matches no option, so every `list.includes(x)` filter keeps nothing)
export const NONE = '__none__';

export const corridorKeyOf = (c) => `${c.route}|${c.pathId}`;
export const corridorLabelOf = (c) =>
  `${c.road ? `${c.road} · ` : ''}${c.route} · ${c.pathId} (${Object.keys(c.directions || {}).sort().join('/')})`;

export function MultiPicker({ label, options, selected, onChange, loading, disabled, noun = 'items',
  allMeansEmpty = false, title, wide = false, extraToggle = null, tall = false, isolate = false }) {
  // isolate: each option has an "only" button; an option with empty: true is greyed (nothing to show under the other filters)
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  // tall: the open list takes the screen height below it (at least the usual 16rem), so a
  // long list shows as many options as fit instead of scrolling inside a small box
  const listRef = useRef(null);
  const [listMax, setListMax] = useState(null);
  useLayoutEffect(() => {
    if (!open || !tall || !listRef.current) return;
    const top = listRef.current.getBoundingClientRect().top;
    setListMax(Math.max(256, Math.floor(window.innerHeight - top - 64)));
  }, [open, tall]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const all = options || [];
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
  const picked = selected.filter((k) => k !== NONE);
  const isAll = picked.length === all.length && all.length > 0;
  const effective = allMeansEmpty && !selected.length ? all.map((o) => o.key) : picked;
  const toggle = (key) => {
    const next = effective.includes(key) ? effective.filter((k) => k !== key) : [...effective, key];
    onChange(!allMeansEmpty ? next : next.length === all.length ? [] : next.length ? next : [NONE]);
  };
  const summary = loading ? 'Loading…'
    : !all.length ? `No ${noun}`
      : (!picked.length && !allMeansEmpty) ? `Select ${noun}…`
        : (isAll || (allMeansEmpty && !selected.length)) ? `All ${noun}`
          : !picked.length ? `No ${noun}`
            : picked.length === 1
              ? (all.find((o) => o.key === picked[0]) || { label: picked[0] }).label
              : `${picked.length} of ${all.length} ${noun}`;

  return (
    <div className={`safety-filter safety-corridor-picker ${wide ? '' : 'safety-picker-narrow'}`} ref={ref} title={title}>
      <span>{label}</span>
      <button type="button" className="safety-picker-btn" disabled={disabled || loading || !all.length}
        onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="safety-picker-text">{summary}</span>
        <span className="safety-picker-caret">▾</span>
      </button>
      {open && (
        <div className="safety-picker-menu" role="listbox" aria-multiselectable="true">
          <div className="safety-picker-tools">
            {all.length > 8 && (
              <input type="search" placeholder="Filter…" value={query}
                onChange={(e) => setQuery(e.target.value)} autoFocus />
            )}
            <button type="button" className="safety-btn safety-btn-ghost"
              onClick={() => onChange(allMeansEmpty ? [] : all.map((o) => o.key))}>Select All</button>
            <button type="button" className="safety-btn safety-btn-ghost"
              onClick={() => onChange(allMeansEmpty ? [NONE] : [])}>Clear All</button>
          </div>
          <div className="safety-picker-list" ref={listRef} style={tall && listMax ? { maxHeight: listMax } : undefined}>
            {shown.map((o) => {
              const on = effective.includes(o.key);
              return (
                <label key={o.key} className={`safety-picker-item ${on ? 'on' : ''} ${o.empty ? 'so-picker-empty' : ''}`} role="option" aria-selected={on}>
                  <input type="checkbox" checked={on} onChange={() => toggle(o.key)} />
                  <span>{o.label}</span>
                  {isolate && (
                    <button type="button" className="so-only" title={`Show only ${o.key}`}
                      onClick={(e) => { e.preventDefault(); onChange([o.key]); setOpen(false); }}>only</button>
                  )}
                </label>
              );
            })}
            {!shown.length && <div className="safety-muted safety-picker-empty">No match.</div>}
          </div>
          {extraToggle && (
            <label className="safety-picker-item safety-picker-extra" htmlFor={extraToggle.id}>
              <input id={extraToggle.id} type="checkbox" checked={extraToggle.checked}
                onChange={(e) => extraToggle.onChange(e.target.checked)} />
              <span>{extraToggle.label}</span>
            </label>
          )}
          <div className="safety-picker-foot safety-muted">
            {effective.length} selected · click outside to close
          </div>
        </div>
      )}
    </div>
  );
}

export default function CorridorPicker({ corridors, selected, onChange, loading, disabled }) {
  const options = (corridors || []).map((c) => ({ key: corridorKeyOf(c), label: corridorLabelOf(c) }));
  return (
    <MultiPicker label="Corridors" options={options} selected={selected} onChange={onChange}
      loading={loading} disabled={disabled} noun="corridors" wide
      title="Choose one or more surveyed corridors" />
  );
}
